// Send a draft out.
//
// Two fields and a warning, and the warning is the substance: issuing is the
// one act on this screen that cannot be undone. Afterwards the document says
// what it says — its header and its lines are fixed by the database, correcting
// it means a credit note, and it cannot be deleted, because a number that went
// out has to keep resolving to the thing it went out on.
//
// AND IT IS THE MOMENT A FIGURE STOPS MOVING. Until it is issued, a draft holds
// no figures at all: its lines are the deal's statement, re-evaluated on every
// read, so a month that is still open reads higher every day. Issuing snapshots
// that statement onto the lines — quantity, unit, unit price, gross, share,
// charged, the term-line revision and the breakdown that produced them — and
// from then on the period returns the snapshot rather than a fresh
// interpretation. That is why the dialog says it in the warning: the total on
// the sheet behind it is a month-to-date sum until this button is pressed.
//
// `allow_unpriced` is the one waiver, and it is an ANSWER TO A REFUSAL. The
// server refuses a period it could not price in full — naming the line and the
// counts of the facts it could not price — which is what stops a partial month
// going out as a whole one. So the waiver is not on the form until that
// refusal has been read: a checkbox offered on every issue, with no figure
// beside it, reads as a formality, and this one leaves money out of a document
// that can never be changed again. Once the refusal is on screen the checkbox
// appears beside it, and it is still never a default.
//
// The number comes from QuickBooks, which raised the document. This system does
// not allocate one; it records which one was used, and refuses a duplicate.

import { useCallback, useState, type ReactNode } from "react";
import type { InvoiceListItem } from "@crm/shared";
import styles from "./InvoiceSheet.module.css";
import { Dialog } from "../../design/Dialog.js";
import { Notice, ProblemMessage } from "../../design/Feedback.js";
import { CheckboxField, DateField, TextField } from "../../forms/fields.js";
import { useIssueInvoiceMutation } from "../../api/mutations.js";
import { useIdempotencyKey } from "../../api/idempotency.js";
import { describeError, isApiProblem } from "../../api/problem.js";
import { todayIsoDate } from "../../format/calendarDate.js";
import { formatCount } from "../../format/money.js";

export interface IssueInvoiceDialogProps {
  invoice: InvoiceListItem;
  onClose: () => void;
}

export function IssueInvoiceDialog({ invoice, onClose }: IssueInvoiceDialogProps): ReactNode {
  const [number, setNumber] = useState("");
  const [issueDate, setIssueDate] = useState(todayIsoDate());
  const [allowUnpriced, setAllowUnpriced] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  /** The server's refusal of a period it could not price, or none yet. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const issue = useIssueInvoiceMutation();
  const idempotency = useIdempotencyKey();

  const submit = useCallback(() => {
    setFailure(null);
    const body = {
      invoice_number: number.trim(),
      issue_date: issueDate,
      // Sent only when it was ticked. A `false` in the body says the same thing
      // as its absence, and an explicit one would make every issue look like a
      // decision somebody took about pricing.
      ...(allowUnpriced ? { allow_unpriced: true } : {}),
    };
    issue.mutate(
      // Keyed off the body, so a double-click or a retry after a dropped
      // response cannot issue the same document twice under two numbers.
      { id: invoice.id, body, idempotencyKey: idempotency.keyFor(body) },
      {
        onError: (error) => {
          setFailure(error);
          // THE ONE REFUSAL THIS DIALOG CAN OFFER A WAY PAST. Its message
          // names the line and the unpriced counts, so the choice is made
          // against a figure rather than against a checkbox.
          if (isApiProblem(error) && error.code === "crm-invoice-state-invalid") {
            setRefusal(error.title);
          }
        },
      },
    );
  }, [issue, invoice.id, number, issueDate, allowUnpriced, idempotency]);

  const described = failure === null ? null : describeError(failure);
  const result = issue.data;

  // The receipt. It replaces the form rather than sitting under it: the
  // document has gone out, so there is nothing left to fill in, and a form
  // still on screen invites a second attempt at a thing that cannot happen
  // twice.
  if (result !== undefined) {
    return (
      <Dialog
        open
        narrow
        onClose={onClose}
        title="Issue this invoice"
        footer={
          <button type="button" className={styles.dialogSubmit} onClick={onClose}>
            Done
          </button>
        }
      >
        <Notice tone="info" title={`Issued as ${result.invoice.invoice_number ?? "—"}.`}>
          {`${formatCount(result.invoice.line_count)} line${
            result.invoice.line_count === 1 ? "" : "s"
          } now carry this number. Every period on it returns its snapshot from here on, so ` +
            `nothing on this document can move again.`}
        </Notice>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      narrow
      onClose={onClose}
      title="Issue this invoice"
      onSubmit={submit}
      footer={
        <>
          <button type="button" className={styles.dialogCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.dialogSubmit}
            disabled={number.trim() === "" || issue.isPending}
          >
            {issue.isPending ? "Issuing…" : "Issue"}
          </button>
        </>
      }
    >
      <Notice tone="warn" title="This cannot be undone.">
        {`Once issued, ${invoice.partner_name}'s invoice is fixed — its lines and header cannot ` +
          `change, and it cannot be deleted. A correction is a credit note. Issuing also ` +
          `snapshots each line's figure onto the document, so every period on it stops moving.`}
      </Notice>

      <TextField
        label="Invoice number"
        required
        hint="The number QuickBooks gave it. It must not already be in use — this is what makes reconciliation a join rather than a search."
        value={number}
        onChange={setNumber}
        {...(described?.code === "crm-conflict"
          ? { error: "Another invoice already has that number." }
          : {})}
      />

      <DateField
        label="Issue date"
        required
        hint="When the document went out. Defaults to today."
        value={issueDate}
        onChange={setIssueDate}
      />

      {refusal !== null && (
        <CheckboxField
          label="Issue even where the facts could not all be priced"
          checked={allowUnpriced}
          onChange={setAllowUnpriced}
          hint="The refusal above names the line and how many facts it could not price. Tick this only if that is a figure you accept: the document goes out with those facts left out of the amount, and it cannot be changed afterwards."
        />
      )}

      {described !== null && described.code !== "conflict" && (
        <ProblemMessage error={failure} action="issue this invoice" />
      )}
    </Dialog>
  );
}
